import { API_BASE_URL, apiRequest, getCurrentAccessToken } from '@/shared/lib/api-client';

export type MediaType = 'image' | 'audio' | 'video' | 'document';

export interface LibraryMedia {
  id: number;
  label: string;
  original_name: string | null;
  mime_type: string;
  type: MediaType;
  size_bytes: number | null;
  duration_ms: number | null;
  created_at: string;
  /** Link durável (HMAC, sem expiração/login) pra reusar em configs/envios. Null se não `stored`. */
  link: string | null;
}

export interface ListLibraryParams {
  q?: string;
  type?: MediaType;
  cursor?: string;
  limit?: number;
}

export interface ListLibraryResponse {
  items: LibraryMedia[];
  next_cursor: string | null;
}

export function listLibrary(params: ListLibraryParams = {}): Promise<ListLibraryResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.type) qs.set('type', params.type);
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return apiRequest<ListLibraryResponse>(`/media/library${query ? `?${query}` : ''}`);
}

/**
 * Upload de mídia pra biblioteca. Label obrigatório (galeria não aceita item
 * sem nome). duration_sec opcional pra áudio gravado in-place.
 */
export async function uploadLibrary(
  file: Blob,
  label: string,
  filename?: string,
  opts?: { durationSec?: number },
): Promise<LibraryMedia> {
  const fd = new FormData();
  // `label` ANTES do `file`: fastify-multipart só popula `file.fields` com
  // campos que vêm antes do arquivo no corpo multipart. Com o file primeiro,
  // o label não entrava em file.fields → backend respondia LABEL_REQUIRED.
  fd.append('label', label);
  fd.append('file', file, filename ?? (file instanceof File ? file.name : 'upload'));
  const token = getCurrentAccessToken();
  // Redundância defensiva: label também na query (backend lê como fallback),
  // cobrindo qualquer variação de parsing do multipart.
  const params = new URLSearchParams({ label });
  if (opts?.durationSec && opts.durationSec > 0) {
    params.set('duration_sec', opts.durationSec.toFixed(2));
  }
  const qs = `?${params.toString()}`;
  const res = await fetch(`${API_BASE_URL}/media/library${qs}`, {
    method: 'POST',
    body: fd,
    credentials: 'include',
    ...(token && { headers: { authorization: `Bearer ${token}` } }),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `upload failed (${res.status})`);
  }
  return res.json();
}

export function renameLibrary(id: number, label: string): Promise<{ ok: true }> {
  return apiRequest<{ ok: true }>(`/media/library/${id}`, {
    method: 'PATCH',
    body: { label },
  });
}

export function deleteLibrary(id: number): Promise<void> {
  return apiRequest<void>(`/media/library/${id}`, { method: 'DELETE' });
}

export interface MediaUrlResponse {
  url: string;
  mime_type: string;
  ttl: number;
  size_bytes: number | null;
  duration_ms: number | null;
}

export function getMediaUrlById(id: number): Promise<MediaUrlResponse> {
  return apiRequest<MediaUrlResponse>(`/media/${id}/url`);
}
